import { jsPDF } from 'jspdf';
import ExcelJS from 'exceljs';
import {
    ReportGenerationRequest,
    REPORT_TYPE,
    LastCargoesReportData,
    REPORT_TYPE_FILE_FORMAT,
    ManifoldPlanReportData,
    TankplanReportData,
    ReportGenerationResponse,
    ManifestReportData,
    CargoEventReportData,
} from './ReportGeneratorTypes';
import { generateLastCargoesReportExcel } from './reportGenerators/lastCargoesReportGenerator';
import { generateManifoldPlanReport } from './reportGenerators/manifoldPlanReportGenerator';
import { generateManifestsReport } from './reportGenerators/manifestReportGenerator';
import { generateTankplanReport } from './reportGenerators/tankplanReportGenerator';
import { generateCargoEventReport } from './reportGenerators/reportEventGenerators/cargoEventReport';
import { generateNorTenderReport } from './reportGenerators/reportEventGenerators/norTenderReport';
import { EventInputEnum } from 'src/enums/eventInputEnum';
import { generateProtestDiffReport } from './reportGenerators/reportEventGenerators/protestDiffReport';
import { generateSlowRateReport } from './reportGenerators/reportEventGenerators/slowRateReport';
import { generateProtestDeadfreightClaimReport } from './reportGenerators/reportEventGenerators/protestDeadfreightClaimReport';
import { generateGeneralProtestReport } from './reportGenerators/reportEventGenerators/generalProtestReport';
import { generateProtestInsufficientNumberHosesReport } from './reportGenerators/reportEventGenerators/protestInsufficientNumberHosesReport';
import { generateCertificateOfCleanlinessReport } from './reportGenerators/reportEventGenerators/certificateOfCleanlinessReport';
import { generateEmptyTankCertificateReport } from './reportGenerators/reportEventGenerators/emptyTankCertificateReport';
import { generateProtestUseOfVesselHosesReport } from './reportGenerators/reportEventGenerators/protestUseOfVesselHosesReport';
import { generateProtestFreePratiqueReport } from './reportGenerators/reportEventGenerators/protestFreePratiqueReport';

/**
 * Generates a PDF or Excel report based on the request data
 * @param request - The PDF generation request
 * @returns Promise resolving to a base64 data URI string
 */
export async function generateReport(requests: ReportGenerationRequest[]): Promise<ReportGenerationResponse> {
    let firstPage = true;

    let doc: jsPDF | undefined;
    let workbook: ExcelJS.Workbook | undefined;
    let logoImageId: number | undefined;

    if (REPORT_TYPE_FILE_FORMAT[requests[0].reportType] === 'pdf') {
        // Create PDF document with provided options
        const options = requests[0].options;
        doc = new jsPDF({
            orientation: options?.orientation || 'portrait',
            unit: 'mm',
            format: options?.format || 'a4',
            compress: true,
        });
    } else {
        // Create Excel workbook
        workbook = new ExcelJS.Workbook();
        try {
            // Get image by URL using fetch instead of file path
            const _logoUrl = '/assets/logos/odfjell_logo.png';
            const _imageBuffer = await fetchImageAsArrayBuffer(_logoUrl);

            logoImageId = workbook.addImage({
                buffer: _imageBuffer,
                extension: 'png',
            });
        } catch (imageError) {
            console.warn('Failed to load logo image:', imageError);
            // Continue without logo if image loading fails
        }
    }

    try {
        for (const request of requests) {
            const { reportType, data } = request;

            // Generate different types of reports based on the report type
            switch (reportType) {
                case REPORT_TYPE.MANIFESTS:
                    if (!doc) throw new Error('PDF document not initialized for MANIFESTS report');
                    generateManifestsReport(doc, data as ManifestReportData, firstPage);
                    break;

                case REPORT_TYPE.TANK_PLANS:
                    if (!doc) throw new Error('PDF document not initialized for TANK_PLANS report');
                    generateTankplanReport(doc, data as TankplanReportData, firstPage);
                    break;

                case REPORT_TYPE.MANIFOLD_PLAN:
                    if (!workbook) throw new Error('Excel workbook not initialized for MANIFOLD_PLAN report');
                    generateManifoldPlanReport(workbook, data as ManifoldPlanReportData, logoImageId);
                    break;

                case REPORT_TYPE.LAST_CARGOES:
                    if (!workbook) throw new Error('Excel document not initialized for LAST_CARGOES report');
                    generateLastCargoesReportExcel(workbook, data as LastCargoesReportData, logoImageId);
                    break;

                case REPORT_TYPE.CARGO_EVENT:
                    if (!workbook) throw new Error('Excel document not initialized for CARGO_EVENT report');
                    generateCargoEventReport(workbook, data as CargoEventReportData);
                    break;
                case REPORT_TYPE.NOR_TENDER:
                case REPORT_TYPE.RE_TENDER_NOR:
                    {
                        if (!workbook) throw new Error('Excel document not initialized for NOR_TENDER report');
                        const eventInputId =
                            reportType === REPORT_TYPE.NOR_TENDER ? EventInputEnum.NORTenderCargoInputId : EventInputEnum.ReTenderNORCargoInputId;
                        generateNorTenderReport(workbook, data as CargoEventReportData, logoImageId, eventInputId);
                    }
                    break;
                case REPORT_TYPE.PROTEST_DIFF:
                    if (!workbook) throw new Error('Excel document not initialized for PROTEST_DIFF report');
                    generateProtestDiffReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.SLOW_RATE:
                    if (!workbook) throw new Error('Excel document not initialized for SLOW_RATE report');
                    generateSlowRateReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.PROTEST_DEADFREIGHT_CLAIM:
                    if (!workbook) throw new Error('Excel document not initialized for PROTEST_DEADFREIGHT_CLAIM report');
                    generateProtestDeadfreightClaimReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.GENERAL_PROTEST:
                    if (!workbook) throw new Error('Excel document not initialized for GENERAL_PROTEST report');
                    generateGeneralProtestReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.INSUFFICIEN_NUMBER_HOSES_REPORT:
                    if (!workbook) throw new Error('Excel document not initialized for INSUFFICIEN_NUMBER_HOSES_REPORT report');
                    generateProtestInsufficientNumberHosesReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.CERTIFICATE_OF_CLEANLINESS:
                    if (!workbook) throw new Error('Excel document not initialized for CERTIFICATE_OF_CLEANLINESS report');
                    generateCertificateOfCleanlinessReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.EMPTY_TANK_CERTIFICATE:
                    if (!workbook) throw new Error('Excel document not initialized for EMPTY_TANK_CERTIFICATE report');
                    generateEmptyTankCertificateReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.PROTEST_USE_OF_VESSEL_HOSES:
                    if (!workbook) throw new Error('Excel document not initialized for PROTEST_USE_OF_VESSEL_HOSES report');
                    generateProtestUseOfVesselHosesReport(workbook, data as CargoEventReportData, logoImageId);
                    break;
                case REPORT_TYPE.PROTEST_FREE_PRATIQUE:
                    if (!workbook) throw new Error('Excel document not initialized for PROTEST_FREE_PRATIQUE report');
                    generateProtestFreePratiqueReport(workbook, data as CargoEventReportData, logoImageId);
                    break;

                default:
                    throw new Error(`Unsupported report type: ${reportType}`);
            }
            firstPage = false;
        }
        // Return appropriate output format
        if (REPORT_TYPE_FILE_FORMAT[requests[0].reportType] === 'pdf' && doc) {
            return {
                success: true,
                dataUri: doc.output('datauristring'),
                mimeType: 'application/pdf',
                fileExtension: 'pdf',
            };
        } else if (workbook) {
            // Browser-compatible approach for Excel files
            const _buffer = await workbook.xlsx.writeBuffer();

            // Convert array buffer to base64 string
            const _uint8Array = new Uint8Array(_buffer);
            let _binaryString = '';

            for (let i = 0; i < _uint8Array.length; i++) {
                _binaryString += String.fromCharCode(_uint8Array[i]);
            }

            return {
                success: true,
                dataUri: `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${btoa(_binaryString)}`,
                mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                fileExtension: 'xlsx',
            };
        } else {
            throw new Error('No document or workbook was created');
        }
    } catch (error) {
        console.error('Error generating report:', error);
        throw error;
    }
}

/**
 * Fetches an image from a URL and returns it as an ArrayBuffer
 * @param imageUrl - URL of the image to fetch
 * @returns Promise resolving to ArrayBuffer containing the image data
 */
async function fetchImageAsArrayBuffer(imageUrl: string): Promise<ArrayBuffer> {
    const _response = await fetch(imageUrl);
    if (!_response.ok) {
        throw new Error(`Failed to fetch image: ${_response.status} ${_response.statusText}`);
    }
    return await _response.arrayBuffer();
}
